import { useState } from 'react';
import { Button } from '../components/Button';
import { Body, Screen, ScreenBody, Title } from '../components/Screen';
import { copy } from '../copy';
import { copyCurrentUrl, detectBrowserContext, openInSystemBrowser } from '../lib/inAppBrowser';
import { noteEvent } from '../lib/diagnostics';

/**
 * Web step 3. The Instagram/TikTok webview interstitial.
 *
 * This is the known drop-off point, so it is a real screen rather than a toast:
 * the guest arrived from an ad inside an app whose webview will either refuse
 * the camera or break the recording, and they get one attempt at this. On
 * Android an intent:// URL genuinely opens Chrome; on iOS nothing reliably
 * escapes the webview, so the copy has to do the work and we offer a copyable
 * link as the fallback.
 *
 * `onContinue` is deliberately available on both platforms — a guest who
 * insists should not be locked out, and a future webview may fix itself.
 */
export function InAppBrowserScreen({ onContinue }: { onContinue: () => void }) {
  const { isAndroid } = detectBrowserContext();
  const [copied, setCopied] = useState(false);

  return (
    <Screen>
      <div className="pt-12" />
      <ScreenBody>
        <Title>{copy.inAppBrowser.heading}</Title>
        <Body>{isAndroid ? copy.inAppBrowser.bodyAndroid : copy.inAppBrowser.bodyIOS}</Body>
        {!isAndroid ? (
          <div className="rounded-2xl border border-bone/20 px-5 py-4 text-left text-sm text-bone/60">
            <p className="mb-2 font-semibold uppercase tracking-wide text-bone/80">
              {copy.inAppBrowser.stepsHeading}
            </p>
            <ol className="list-decimal space-y-1 pl-4">
              <li>{copy.inAppBrowser.stepDots}</li>
              <li>{copy.inAppBrowser.stepOpen}</li>
            </ol>
          </div>
        ) : null}
      </ScreenBody>

      <div className="w-full max-w-sm space-y-3 pb-4">
        {isAndroid ? (
          <Button
            onClick={() => {
              noteEvent('in-app: attempting Chrome handoff');
              if (!openInSystemBrowser()) onContinue();
            }}
          >
            {copy.inAppBrowser.ctaAndroid}
          </Button>
        ) : null}
        <Button
          variant="secondary"
          onClick={() => {
            void copyCurrentUrl().then((ok) => {
              setCopied(ok);
              noteEvent(`in-app: link copy ${ok ? 'ok' : 'failed'}`);
            });
          }}
        >
          {copied ? copy.inAppBrowser.copied : copy.inAppBrowser.ctaCopy}
        </Button>
        <Button variant="ghost" onClick={onContinue}>
          {copy.inAppBrowser.ctaContinue}
        </Button>
      </div>
    </Screen>
  );
}
